<template>
  <div class="team-edit-page basketball-edit-page">
    <MainNavbar />

    <!-- Toast Notifications -->
    <ToastContainer :toasts="toasts" @remove="removeToast" />
    
    <!-- Page Header -->
    <div class="page-header">
      <div class="page-header-row">
        <router-link :to="{ name: 'BasketballTeamDashboard' }" class="back-to-dashboard">
          <i class="fa-solid fa-arrow-left" aria-hidden="true" />
          Back to Dashboard
        </router-link>
      </div>
      <h1><span class="sport-pill sport-pill--basketball">Basketball</span><i class="fa-solid fa-basketball"></i> Edit Basketball Team</h1>
      <p>Update your squad profile, court details, and roster</p>
    </div>

    <!-- Main Content -->
    <div class="edit-content">
      <!-- Team Info Card -->
      <div class="team-info-card card slide-in">
        <div class="card-header">
          <div class="card-title">
            <i class="fa-solid fa-circle-info"></i>
            <h2>Team Information</h2>
          </div>
        </div>

        <div class="card-body">
          <form @submit.prevent="saveTeam" class="team-form">
            <div class="form-grid">
              <!-- Team Logo -->
              <div class="form-group logo-group">
                <label>Team Logo</label>
                <div class="logo-display-wrapper">
                  <div v-if="!form.logo_url" class="logo-display">
                    <i class="fa-solid fa-users"></i>
                    <span>No Logo</span>
                  </div>
                  <img
                    v-else
                    :src="form.logo_url"
                    alt="Team Logo"
                    class="logo-image-display"
                    decoding="async"
                  >
                </div>
                <p class="logo-info">Team logo (display only)</p>
              </div>

              <!-- Basic Info -->
              <div class="form-group">
                <label for="teamName">
                  <i class="fa-solid fa-flag"></i> Team Name *
                </label>
                <div class="input-wrapper">
                  <input
                    id="teamName"
                    v-model.trim="form.name"
                    type="text"
                    placeholder="Enter team name"
                    :class="{ 'error': errors.name, 'success': form.name && !errors.name }"
                  >
                  <i v-if="form.name && !errors.name" class="fa-solid fa-circle-check input-success-icon"></i>
                </div>
                <span v-if="errors.name" class="error-message">
                  <i class="fa-solid fa-exclamation-circle"></i> {{ errors.name }}
                </span>
              </div>

              <div class="form-group">
                <label for="teamType">
                  <i class="fa-solid fa-tag"></i> Team Type *
                </label>
                <select
                  id="teamType"
                  v-model="form.team_type"
                  :class="{ 'error': errors.team_type, 'success': form.team_type && !errors.team_type }"
                >
                  <option value="">Select type</option>
                  <option value="preschool">Pre-School</option>
                  <option value="highschool">High School</option>
                  <option value="academy">Academy / Youth</option>
                  <option value="recreational">Recreational</option>
                  <option value="competitive">Competitive</option>
                  <option value="corporate">Corporate</option>
                  <option value="school">School</option>
                  <option value="community">Community</option>
                </select>
                <span v-if="errors.team_type" class="error-message">
                  <i class="fa-solid fa-exclamation-circle"></i> {{ errors.team_type }}
                </span>
              </div>

              <div class="form-group">
                <label for="teamPhone">
                  <i class="fa-solid fa-phone"></i> Contact Phone
                </label>
                <input
                  id="teamPhone"
                  v-model.trim="form.phone"
                  type="tel"
                  placeholder="+254712345678"
                >
              </div>

              <div class="form-group">
                <label for="skillLevel">
                  <i class="fa-solid fa-trophy"></i> Skill Level *
                </label>
                <div class="skill-levels">
                  <button
                    v-for="level in skillLevels"
                    :key="level.value"
                    type="button"
                    class="skill-pill"
                    :class="{ 'active': form.skill_level === level.value }"
                    @click="form.skill_level = level.value"
                  >
                    <i :class="level.icon"></i>
                    {{ level.label }}
                  </button>
                </div>
                <span v-if="errors.skill_level" class="error-message">{{ errors.skill_level }}</span>
              </div>

              <div class="form-group full-width">
                <label for="teamBio">
                  <i class="fa-solid fa-comment"></i> Team Bio *
                </label>
                <textarea
                  id="teamBio"
                  v-model.trim="form.bio"
                  rows="3"
                  placeholder="Tell us about your team..."
                  :class="{ 'error': errors.bio }"
                  maxlength="500"
                ></textarea>
                <div class="char-count">{{ form.bio.length }}/500</div>
                <span v-if="errors.bio" class="error-message">{{ errors.bio }}</span>
              </div>

              <!-- Location (aligned with team registration / API: home_ground, county, constituency) -->
              <div class="form-section">
                <h3><i class="fa-solid fa-location-dot"></i> Location</h3>
                <div class="form-row">
                  <div class="form-group">
                    <label for="homeGround">Home court *</label>
                    <input
                      id="homeGround"
                      v-model.trim="form.home_ground"
                      type="text"
                      placeholder="e.g. Community Sports Arena"
                      :class="{ 'error': errors.home_ground }"
                    >
                    <span v-if="errors.home_ground" class="error-message">{{ errors.home_ground }}</span>
                  </div>

                  <PinLocationEditor
                    ref="pinLocationEditor"
                    :saved-url="savedPinLocation"
                    :venue-query="pinLocationSearchQuery"
                    :error-message="errors.home_ground_pin_location"
                    @clear-error="clearPinLocationError"
                  />

                  <div class="form-group">
                    <label for="county">County *</label>
                    <select
                      id="county"
                      v-model="form.county"
                      :class="{ 'error': errors.county }"
                    >
                      <option value="" disabled>Select county</option>
                      <option
                        v-if="form.county && !availableCounties.includes(form.county)"
                        :value="form.county"
                      >
                        {{ form.county }}
                      </option>
                      <option v-for="c in availableCounties" :key="c" :value="c">{{ c }}</option>
                    </select>
                    <span v-if="errors.county" class="error-message">{{ errors.county }}</span>
                  </div>

                  <div class="form-group">
                    <label for="constituency">Constituency *</label>
                    <select
                      id="constituency"
                      v-model="form.constituency"
                      :disabled="!form.county"
                      :class="{ 'error': errors.constituency }"
                    >
                      <option value="" disabled>{{ form.county ? 'Select constituency' : 'Select county first' }}</option>
                      <option
                        v-if="form.constituency && !availableConstituencies.includes(form.constituency)"
                        :value="form.constituency"
                      >
                        {{ form.constituency }}
                      </option>
                      <option v-for="co in availableConstituencies" :key="co" :value="co">{{ co }}</option>
                    </select>
                    <span v-if="errors.constituency" class="error-message">{{ errors.constituency }}</span>
                  </div>
                </div>
              </div>

              <!-- Basketball format -->
              <div class="form-section">
                <h3><i class="fa-solid fa-basketball"></i> Game &amp; court setup</h3>
                <div class="form-row">
                  <div class="form-group">
                    <label for="courtType">Court type *</label>
                    <select
                      id="courtType"
                      v-model="form.court_type"
                      :class="{ 'error': errors.court_type }"
                    >
                      <option value="">Select court</option>
                      <option value="indoor">Indoor</option>
                      <option value="outdoor">Outdoor</option>
                      <option value="both">Indoor &amp; outdoor</option>
                    </select>
                    <span v-if="errors.court_type" class="error-message">{{ errors.court_type }}</span>
                  </div>

                  <div class="form-group">
                    <label for="gameStructure">Game structure *</label>
                    <select
                      id="gameStructure"
                      v-model="form.game_structure"
                      :class="{ 'error': errors.game_structure }"
                    >
                      <option value="">How you usually play</option>
                      <option value="4x10">4 ? 10-minute quarters (FIBA)</option>
                      <option value="4x12">4 ? 12-minute quarters</option>
                      <option value="2x20">2 ? 20-minute halves</option>
                      <option value="running">Running clock / recreational</option>
                    </select>
                    <span v-if="errors.game_structure" class="error-message">{{ errors.game_structure }}</span>
                  </div>
                </div>
              </div>

              <div class="form-section">
                <h3><i class="fa-solid fa-calendar-check"></i> Schedule</h3>
                <div class="form-group">
                  <label>Preferred practice / game days</label>
                  <div class="days-grid">
                    <button
                      v-for="day in daysOfWeek"
                      :key="day.value"
                      type="button"
                      class="day-pill"
                      :class="{ 'active': form.preferences.preferred_days.includes(day.value) }"
                      @click="toggleDay(day.value)"
                    >
                      {{ day.label }}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div class="form-actions">
              <button type="button" class="btn btn-outline" @click="cancel" :disabled="saving">
                <i class="fa-solid fa-times"></i> Cancel
              </button>
              <button type="submit" class="btn btn-primary" :disabled="saving">
                <i class="fa-solid" :class="saving ? 'fa-spinner fa-spin' : 'fa-save'"></i>
                {{ saving ? 'Saving...' : 'Save Changes' }}
              </button>
            </div>
          </form>
        </div>
      </div>

      <!-- Team Members Card -->
      <div class="members-card card slide-in" style="animation-delay: 0.1s">
        <div class="card-header">
          <div class="card-title">
            <i class="fa-solid fa-users"></i>
            <h2>Team Members ({{ players.length }})</h2>
          </div>
          <button
            v-if="isTeamOwner"
            class="btn btn-primary"
            @click="invitePlayers"
            :disabled="savingPlayer"
          >
            <i class="fa-solid fa-envelope"></i> Invite Players
          </button>
        </div>

        <div class="card-body">
          <!-- Search & Filter -->
          <div class="members-controls">
            <div class="search-box">
              <i class="fa-solid fa-search"></i>
              <input
                v-model.trim="searchQuery"
                type="text"
                placeholder="Search players..."
                @input="debouncedFilter"
                :disabled="savingPlayer"
              >
              <button 
                v-if="searchQuery" 
                class="clear-search"
                @click="searchQuery = ''; filterPlayers()"
                :disabled="savingPlayer"
              >
                <i class="fa-solid fa-times"></i>
              </button>
            </div>

            <div class="filter-tabs">
              <button
                v-for="filter in positionFilters"
                :key="filter.value"
                class="tab-btn"
                :class="{ 'active': activeFilter === filter.value }"
                @click="activeFilter = filter.value"
                :disabled="savingPlayer"
              >
                {{ filter.label }}
                <span class="tab-count">{{ getFilterCount(filter.value) }}</span>
              </button>
            </div>
          </div>

          <!-- Players List -->
          <TransitionGroup name="list" tag="div" class="players-list">
            <div
              v-for="player in paginatedPlayers"
              :key="player.id"
              class="player-card"
              :class="{ 
                'is-captain': player.is_captain,
                'is-owner': player.role === 'owner'
              }"
            >
              <div class="player-avatar">
                <i class="fa-solid fa-user"></i>
                <span v-if="player.is_captain" class="captain-badge">
                  <i class="fa-solid fa-crown"></i>
                </span>
              </div>

              <div class="player-info">
                <div class="player-header">
                  <h4>{{ player.name }}</h4>
                  <div class="player-meta">
                    <span class="player-position" :class="player.position">
                      <i :class="getPositionIcon(player.position)" class="position-icon"></i>
                      {{ formatPosition(player.position) || 'No position' }}
                    </span>
                    <span class="player-role" :class="player.role">
                      {{ formatRole(player.role) }}
                    </span>
                  </div>
                </div>

                <div class="player-stats-grid stats-all">
                  <div class="stat-item">
                    <div class="stat-icon">
                      <i class="fa-solid fa-basketball"></i>
                    </div>
                    <div class="stat-content">
                      <div class="stat-label">Points</div>
                      <div class="stat-value">{{ player.points || 0 }}</div>
                    </div>
                  </div>

                  <div class="stat-item">
                    <div class="stat-icon">
                      <i class="fa-solid fa-arrow-up"></i>
                    </div>
                    <div class="stat-content">
                      <div class="stat-label">Rebounds</div>
                      <div class="stat-value">{{ player.rebounds || 0 }}</div>
                    </div>
                  </div>

                  <div class="stat-item">
                    <div class="stat-icon">
                      <i class="fa-regular fa-handshake"></i>
                    </div>
                    <div class="stat-content">
                      <div class="stat-label">Assists</div>
                      <div class="stat-value">{{ player.assists || 0 }}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="player-actions">
                <button 
                  class="btn-icon btn-edit"
                  @click="editPlayer(player)"
                  title="Edit player details"
                  :disabled="savingPlayer"
                  aria-label="Edit player"
                >
                  <i class="fa-solid fa-pen"></i>
                </button>
                <button 
                  class="btn-icon btn-captain"
                  :class="{ 'is-captain': player.is_captain }"
                  @click="updateCaptain(player)"
                  :title="player.is_captain ? 'Remove as captain' : 'Make captain'"
                  :disabled="savingPlayer || player.role === 'owner'"
                  aria-label="Toggle captain status"
                >
                  <i class="fa-solid" :class="player.is_captain ? 'fa-crown' : 'fa-star'"></i>
                </button>
                <button 
                  v-if="player.role !== 'owner'"
                  class="btn-icon btn-danger"
                  @click="confirmDeletePlayer(player)"
                  title="Remove player from team"
                  :disabled="savingPlayer"
                  aria-label="Remove player"
                >
                  <i class="fa-solid fa-trash"></i>
                </button>
              </div>
            </div>

            <div v-if="filteredPlayers.length === 0" key="empty" class="empty-state">
              <div class="empty-icon">
                <i :class="searchQuery ? 'fa-solid fa-search' : 'fa-solid fa-users'"></i>
              </div>
              <h3>{{ searchQuery ? 'No players match your search' : 'No players yet' }}</h3>
              <p v-if="searchQuery" class="empty-description">Try adjusting your search or filter criteria</p>
              <p v-else class="empty-description">
                Invite players with IpromiseAfrica accounts so performance stats stay linked to their profiles.
              </p>
              <button
                v-if="isTeamOwner && !searchQuery"
                class="btn btn-primary"
                @click="invitePlayers"
                :disabled="savingPlayer"
              >
                <i class="fa-solid fa-envelope"></i> Invite Players
              </button>
              <button
                v-else-if="searchQuery"
                class="btn btn-primary"
                @click="onEmptyPlayersPrimaryAction"
                :disabled="savingPlayer"
              >
                <i class="fa-solid fa-times"></i> Clear Search
              </button>
            </div>
          </TransitionGroup>

          <!-- Pagination -->
          <div v-if="filteredPlayers.length > itemsPerPage" class="pagination">
            <button 
              class="pagination-btn"
              :disabled="currentPage === 1 || savingPlayer"
              @click="goToPage(currentPage - 1)"
            >
              <i class="fa-solid fa-chevron-left"></i>
            </button>
            
            <div class="page-numbers">
              <button
                v-for="page in visiblePages"
                :key="page"
                class="page-btn"
                :class="{ 'active': currentPage === page }"
                @click="goToPage(page)"
                :disabled="savingPlayer"
              >
                {{ page }}
              </button>
              <span v-if="showEllipsis" class="page-ellipsis">...</span>
            </div>
            
            <button 
              class="pagination-btn"
              :disabled="currentPage === totalPages || savingPlayer"
              @click="goToPage(currentPage + 1)"
            >
              <i class="fa-solid fa-chevron-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Player Modal (position only ? roster via invites) -->
    <Modal
      :show="showEditPlayer"
      title="Edit Player"
      icon="fa-pen"
      @close="closePlayerModal"
      :disabled="savingPlayer"
    >
      <form @submit.prevent="savePlayer" class="player-form">
        <div v-if="editingPlayer" class="form-group">
          <label>Player</label>
          <p class="player-readonly-name">{{ editingPlayer.name }}</p>
          <p v-if="editingPlayer.email" class="player-readonly-meta">{{ editingPlayer.email }}</p>
        </div>

        <div class="form-group">
          <label>Position</label>
          <select v-model="playerForm.position" :disabled="savingPlayer">
            <option value="">Select position</option>
            <option value="pg">Point guard (PG)</option>
            <option value="sg">Shooting guard (SG)</option>
            <option value="sf">Small forward (SF)</option>
            <option value="pf">Power forward (PF)</option>
            <option value="c">Center (C)</option>
            <option value="flex">Flex / utility</option>
          </select>
        </div>
      </form>

      <template #footer>
        <button type="button" class="btn btn-outline" @click="closePlayerModal" :disabled="savingPlayer">
          Cancel
        </button>
        <button type="button" class="btn btn-primary" @click="savePlayer" :disabled="savingPlayer || !editingPlayer">
          <i class="fa-solid" :class="savingPlayer ? 'fa-spinner fa-spin' : 'fa-save'"></i>
          {{ savingPlayer ? 'Saving...' : 'Save Changes' }}
        </button>
      </template>
    </Modal>

    <!-- Confirmation Modal -->
    <Modal 
      :show="showConfirmDelete"
      title="Remove Player"
      icon="fa-trash"
      @close="showConfirmDelete = false"
    >
      <div class="confirmation-content">
        <i class="fa-solid fa-exclamation-triangle warning-icon"></i>
        <p>Are you sure you want to remove <strong>{{ playerToDelete?.name }}</strong> from the team?</p>
        <p class="warning-text">This action cannot be undone.</p>
      </div>
      
      <template #footer>
        <button type="button" class="btn btn-outline" @click="showConfirmDelete = false">
          Cancel
        </button>
        <button type="button" class="btn btn-danger" @click="deletePlayer">
          <i class="fa-solid fa-trash"></i> Remove Player
        </button>
      </template>
    </Modal>
  </div>
</template>

<script>
  import MainNavbar from "@/components/main/headernfooter/header.vue";
  import globalLoadingMixin from "@/mixins/globalLoadingMixin";
  import ToastContainer from "@/components/ui/ToastContainer.vue";
import toastMixin from "@/mixins/toastMixin";
  import Modal from "@/components/ui/Modal.vue";
  import PinLocationEditor from "@/routes/match_hub/shared/PinLocationEditor.vue";
  import { buildApiUrl } from "@/config/api";
  import { kenyaCounties } from "../football/counties.js";
  import { useAuthStore } from "@/authen/auth";
  import { verifyBasketballTeamOwnerAccess } from "@/utils/matchHubAccess";

  const OWNER_TEAM_ACTION_DENIED_MESSAGE = "Sorry unable to complete request";
  const OWNER_ACTION_DENIED = "OWNER_ACTION_DENIED";
  
  export default {
    name: 'BasketballTeamEdit',
  mixins: [toastMixin, globalLoadingMixin],
    components: { 
      MainNavbar,
      ToastContainer,
      Modal,
      PinLocationEditor,
    },
    
    data() {
      return {
        skipEditInit: false,
        teamAccessPromise: null,
        loading: false,
        loadingMessage: 'Loading team details...',
        saving: false,
        savingPlayer: false,
        
        // Toast notifications
        
        // Team Data
        team: null,
        savedPinLocation: "",

        // Team Form (fields align with CreateTeamForm / TeamUpdateForm in API)
        form: {
          id: null,
          name: '',
          team_type: '',
          phone: '',
          bio: '',
          logo_url: null,
          home_ground: '',
          county: '',
          constituency: '',
          skill_level: '',
          court_type: '',
          game_structure: '',
          status: 'active',
          preferences: {
            preferred_days: [],
          }
        },

        availableConstituencies: [],
        
        errors: {},
        
        // Players
        players: [],
        filteredPlayers: [],
        searchQuery: '',
        activeFilter: 'all',
        currentPage: 1,
        itemsPerPage: 6,
        debounceTimer: null,
        
        // Player Form (edit position only)
        showEditPlayer: false,
        editingPlayer: null,
        playerForm: {
          position: '',
        },
        
        // Confirmation Modal
        showConfirmDelete: false,
        playerToDelete: null,

        /** Set true after team loads from `/api/teams/basketball/my-team` (owner-only). */
        isTeamOwner: false,
        
        // Data
        skillLevels: [
          { value: 'beginner', label: 'Beginner', icon: 'fa-solid fa-seedling' },
          { value: 'intermediate', label: 'Intermediate', icon: 'fa-solid fa-chart-line' },
          { value: 'advanced', label: 'Advanced', icon: 'fa-solid fa-trophy' },
          { value: 'elite', label: 'Elite', icon: 'fa-solid fa-crown' }
        ],
        
        daysOfWeek: [
          { value: 'monday', label: 'Mon' },
          { value: 'tuesday', label: 'Tue' },
          { value: 'wednesday', label: 'Wed' },
          { value: 'thursday', label: 'Thu' },
          { value: 'friday', label: 'Fri' },
          { value: 'saturday', label: 'Sat' },
          { value: 'sunday', label: 'Sun' }
        ],
        
        positionFilters: [
          { value: 'all', label: 'All' },
          { value: 'pg', label: 'PG' },
          { value: 'sg', label: 'SG' },
          { value: 'sf', label: 'SF' },
          { value: 'pf', label: 'PF' },
          { value: 'c', label: 'C' },
          { value: 'flex', label: 'Flex' },
        ],

        positionIcons: {
          pg: 'fa-solid fa-basketball',
          sg: 'fa-solid fa-bolt',
          sf: 'fa-solid fa-person-running',
          pf: 'fa-solid fa-shield-halved',
          c: 'fa-solid fa-arrows-up-down',
          flex: 'fa-solid fa-users',
        }
      };
    },
    
    computed: {
      totalPages() {
        return Math.max(1, Math.ceil(this.filteredPlayers.length / this.itemsPerPage));
      },
      
      paginatedPlayers() {
        const start = (this.currentPage - 1) * this.itemsPerPage;
        const end = start + this.itemsPerPage;
        return this.filteredPlayers.slice(start, end);
      },
      
      /** Route: `/ipromiseafrica/matchup/basketball/team-edit/:s/edit` ? `s` is team id or name. */
      routeTeamSlugOrId() {
        const s = this.$route.params.s;
        return s === undefined || s === null ? null : String(s);
      },

      availableCounties() {
        return Object.keys(kenyaCounties);
      },

      pinLocationSearchQuery() {
        return [this.form.home_ground, this.form.county, this.form.constituency]
          .map((part) => (part || "").trim())
          .filter(Boolean)
          .join(", ");
      },
      
      visiblePages() {
        const pages = [];
        const maxVisible = 5;
        
        if (this.totalPages <= maxVisible) {
          for (let i = 1; i <= this.totalPages; i++) {
            pages.push(i);
          }
        } else {
          let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
          let end = start + maxVisible - 1;
          
          if (end > this.totalPages) {
            end = this.totalPages;
            start = Math.max(1, end - maxVisible + 1);
          }
          
          for (let i = start; i <= end; i++) {
            pages.push(i);
          }
        }
        
        return pages;
      },
      
      showEllipsis() {
        return this.totalPages > 5 && this.currentPage < this.totalPages - 2;
      }
    },
    
    beforeMount() {
      const auth = useAuthStore();
      auth.checkToken();
      if (!auth.isAuthenticated) {
        this.skipEditInit = true;
        this.$router.replace({
          name: "CustomerSignIn",
          query: { redirect: this.$route.fullPath },
        });
        return;
      }
      this.teamAccessPromise = verifyBasketballTeamOwnerAccess();
    },

    async mounted() {
      if (this.skipEditInit) return;
      const result = await this.teamAccessPromise;
      if (!result?.ok) {
        if (result?.reason === "session" || result?.reason === "unauthenticated") {
          this.$router.replace({
            name: "CustomerSignIn",
            query: { redirect: this.$route.fullPath },
          });
        } else {
          this.$router.replace({ name: "MatchesHub" });
        }
        return;
      }
      if (!result.has_customer || !result.has_team) {
        this.$router.replace({ name: "BasketballTeamRegistration" });
        return;
      }
      await this.loadTeamData();
    },
    
    watch: {
      activeFilter() {
        this.currentPage = 1;
        this.filterPlayers();
      },

      "form.county"(val) {
        this.availableConstituencies = val && kenyaCounties[val] ? [...kenyaCounties[val]] : [];
        if (
          this.form.constituency &&
          this.availableConstituencies.length &&
          !this.availableConstituencies.includes(this.form.constituency)
        ) {
          this.form.constituency = "";
        }
      },
    },
    
    methods: {
      // ========== HELPER METHODS ==========
      async getAuthToken() {
        return localStorage.getItem('access_token') || 
               sessionStorage.getItem('access_token');
      },
      
      debouncedFilter() {
        if (this.debounceTimer) clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => {
          this.currentPage = 1;
          this.filterPlayers();
        }, 300);
      },
      
      formatPosition(position) {
        if (!position) return '';
        const labels = {
          pg: 'Point Guard',
          sg: 'Shooting Guard',
          sf: 'Small Forward',
          pf: 'Power Forward',
          c: 'Center',
          flex: 'Flex',
        };
        const key = String(position).toLowerCase();
        return labels[key] || position.charAt(0).toUpperCase() + position.slice(1);
      },
      
      formatRole(role) {
        const roles = {
          'owner': 'Owner',
          'captain': 'Captain',
          'vice_captain': 'Vice Captain',
          'player': 'Player'
        };
        return roles[role] || role;
      },
      
      getPositionIcon(position) {
        if (!position) return '';
        return this.positionIcons[position.toLowerCase()] || '';
      },
      
      getFilterCount(filter) {
        if (filter === 'all') return this.players.length;
        return this.players.filter(p => p.position && p.position.toLowerCase() === filter).length;
      },
      
      shouldShowStat(position, statType) {
        if (!position) return true; // Show all if no position
        
        const pos = position.toLowerCase();
        
        // Forward and Midfielder: show goals and assists
        if (pos === 'forward' || pos === 'midfielder') {
          return statType === 'goals' || statType === 'assists';
        }
        
        // Defender and Goalkeeper: show clean sheets only
        if (pos === 'defender' || pos === 'goalkeeper') {
          return statType === 'clean_sheets';
        }
        
        // Utility: show all stats
        if (pos === 'utility') {
          return true;
        }
        
        // Default: show all if position doesn't match
        return true;
      },
      
      getStatsGridClass(position) {
        if (!position) return 'stats-all';
        
        const pos = position.toLowerCase();
        
        if (pos === 'forward' || pos === 'midfielder') {
          return 'stats-two';
        }
        
        if (pos === 'defender' || pos === 'goalkeeper') {
          return 'stats-one';
        }
        
        return 'stats-all';
      },
      showOwnerActionDeniedToast() {
        this.showToast("error", "", OWNER_TEAM_ACTION_DENIED_MESSAGE);
      },

      // ========== TEAM METHODS ==========
      routeMatchesTeam(team, rawParam) {
        if (rawParam === null || rawParam === undefined || rawParam === "") return true;
        const decoded = decodeURIComponent(String(rawParam).trim());
        const n = Number(rawParam);
        if (!Number.isNaN(n) && n === team.team_id) return true;
        const name = (team.name || "").trim();
        return name === decoded.trim() || name === String(rawParam).trim();
      },

      normalizeApiSelectValue(value) {
        if (value === null || value === undefined) return "";
        return String(value).trim().toLowerCase();
      },

      async loadTeamData(options = {}) {
        const { silent = false } = options;
        if (!silent) {
          this.loading = true;
          this.loadingMessage = "Loading team details...";
        }
        try {
          const teamPayload = await this.fetchMyTeamWithMembers();
          if (!teamPayload || teamPayload.team_id == null) {
            this.showToast("error", "No team found. Redirecting to dashboard...");
            setTimeout(() => {
              this.$router.push({ name: "BasketballTeamDashboard" });
            }, 1500);
            return;
          }

          const slug = this.routeTeamSlugOrId;
          if (slug && !this.routeMatchesTeam(teamPayload, slug)) {
            this.showToast("error", "This link does not match your team. Opening your dashboard.");
            setTimeout(() => {
              this.$router.replace({ name: "BasketballTeamDashboard" });
            }, 1500);
            return;
          }

          this.processTeamData(teamPayload, { silent });
        } catch (error) {
          console.error("Error loading team:", error);
          this.showToast("error", "Failed to load team data. Redirecting...");
          setTimeout(() => {
            this.$router.push({ name: "BasketballTeamDashboard" });
          }, 1500);
        } finally {
          if (!silent) {
            this.loading = false;
          }
        }
      },

      /** Re-fetch team + members without redirects (e.g. after save). */
      async refreshTeamSnapshot() {
        try {
          const teamPayload = await this.fetchMyTeamWithMembers();
          if (teamPayload?.team_id != null) {
            this.processTeamData(teamPayload, { silent: true });
          }
        } catch (e) {
          console.warn("Could not refresh team after save:", e);
        }
      },

      processTeamData(teamData, options = {}) {
        const { silent = false } = options;
        try {
          this.team = teamData;
          const prefs = teamData.preferences && typeof teamData.preferences === "object" ? teamData.preferences : {};

          this.form = {
            id: teamData.team_id ?? teamData.team_id_num,
            name: teamData.name || "",
            team_type: this.normalizeApiSelectValue(teamData.team_type),
            phone: teamData.phone || "",
            bio: teamData.bio || "",
            logo_url: teamData.logo_url || null,
            home_ground: teamData.home_ground || "",
            county: teamData.county || "",
            constituency: teamData.constituency || "",
            skill_level: this.normalizeApiSelectValue(teamData.skill_level),
            court_type: teamData.court_type || "",
            game_structure: teamData.game_structure || "",
            status: teamData.is_active !== false ? "active" : "inactive",
            preferences: {
              preferred_days: Array.isArray(prefs.preferred_days) ? [...prefs.preferred_days] : [],
            },
          };

          this.savedPinLocation = teamData.home_ground_pin_location || "";

          if (this.form.county && kenyaCounties[this.form.county]) {
            this.availableConstituencies = [...kenyaCounties[this.form.county]];
          } else if (this.form.constituency) {
            this.availableConstituencies = [this.form.constituency];
          } else {
            this.availableConstituencies = [];
          }

          this.players = this.extractPlayersFromTeamData(teamData);
          this.filterPlayers();
          this.isTeamOwner = true;
          if (!silent) {
            this.showToast("success", "Team loaded successfully");
          }
        } catch (error) {
          this.isTeamOwner = false;
          console.error("Error processing team data:", error);
          this.showToast("error", "Failed to process team data. Redirecting...");
          setTimeout(() => {
            this.$router.push({ name: "BasketballTeamDashboard" });
          }, 1500);
        }
      },

      extractPlayersFromTeamData(teamData) {
        const players = [];

        if (teamData.members && Array.isArray(teamData.members)) {
          teamData.members.forEach((member, index) => {
            const num = member.team_member_number ?? member.member_id;
            if (num === undefined || num === null) return;
            const posRaw = member.position || "";
            const pos = String(posRaw).toLowerCase();
            const roleRaw = member.role || "player";
            const role = String(roleRaw).toLowerCase();
            players.push({
              id: num,
              name: member.name || member.player_name || `Member ${index + 1}`,
              position: pos,
              role,
              status: (member.status || "active").toLowerCase(),
              is_captain: Boolean(member.is_captain),
              email: member.email || member.player_email || "",
              phone: member.phone || member.player_phone || "",
              joined_at: member.joined_at || new Date().toISOString(),
              points: member.points || 0,
              rebounds: member.rebounds || 0,
              assists: member.assists || 0,
            });
          });
        }

        return players;
      },
      
      validateTeamForm() {
        this.errors = {};
        
        if (!this.form.name.trim()) {
          this.errors.name = 'Team name is required';
        }
        
        if (!this.form.team_type) {
          this.errors.team_type = 'Select team type';
        }
        
        if (!this.form.bio.trim()) {
          this.errors.bio = 'Team bio is required';
        }
        
        if (!this.form.home_ground.trim()) {
          this.errors.home_ground = "Home ground is required";
        }

        if (!this.form.county) {
          this.errors.county = "County is required";
        }

        if (!this.form.constituency) {
          this.errors.constituency = "Constituency is required";
        }
        
        if (!this.form.skill_level) {
          this.errors.skill_level = 'Select skill level';
        }

        if (!this.form.court_type) {
          this.errors.court_type = 'Select court type';
        }

        if (!this.form.game_structure) {
          this.errors.game_structure = 'Select game structure';
        }

        const pinResult = this.$refs.pinLocationEditor?.resolveForSave?.() ?? {
          value: (this.savedPinLocation || "").trim() || null,
          error: null,
        };
        if (pinResult.error) {
          this.errors.home_ground_pin_location = pinResult.error;
        }
        
        return Object.keys(this.errors).length === 0;
      },
      
      async saveTeam() {
        if (!this.isTeamOwner) {
          this.showOwnerActionDeniedToast();
          return;
        }
        if (!this.validateTeamForm()) return;

        this.saving = true;
        try {
          const pinResult = this.$refs.pinLocationEditor.resolveForSave();
          const updateData = {
            name: this.form.name.trim(),
            team_type: this.form.team_type,
            phone: this.form.phone.trim() || null,
            bio: this.form.bio.trim(),
            home_ground: this.form.home_ground.trim(),
            home_ground_pin_location: pinResult.value,
            county: this.form.county.trim(),
            constituency: this.form.constituency.trim(),
            skill_level: this.form.skill_level,
            court_type: this.form.court_type,
            game_structure: this.form.game_structure,
            preferences: {
              preferred_days: this.form.preferences.preferred_days,
              sport: "basketball",
            },
          };

          const updated = await this.updateTeam(this.form.id, updateData);

          if (updated?.logo_url) {
            this.form.logo_url = updated.logo_url;
          }
          this.showToast("success", updated?.message || "Team updated successfully");
          await this.refreshTeamSnapshot();
        } catch (error) {
          if (error.message === OWNER_ACTION_DENIED) return;
          console.error("Error saving team:", error);
          this.showToast("error", error.message || "Failed to save team");
        } finally {
          this.saving = false;
        }
      },
      
      clearPinLocationError() {
        delete this.errors.home_ground_pin_location;
      },

      cancel() {
        this.$router.push({ name: "BasketballTeamDashboard" }).catch(() => {});
      },
      
      toggleDay(day) {
        const index = this.form.preferences.preferred_days.indexOf(day);
        if (index > -1) {
          this.form.preferences.preferred_days.splice(index, 1);
        } else {
          this.form.preferences.preferred_days.push(day);
        }
      },
      
      // ========== PLAYER METHODS ==========
      filterPlayers() {
        let filtered = [...this.players];
        
        if (this.searchQuery) {
          const query = this.searchQuery.toLowerCase();
          filtered = filtered.filter(player => 
            player.name.toLowerCase().includes(query) ||
            player.position?.toLowerCase().includes(query) ||
            player.role?.toLowerCase().includes(query) ||
            player.email?.toLowerCase().includes(query)
          );
        }
        
        if (this.activeFilter !== 'all') {
          filtered = filtered.filter(player => {
            if (!player.position) return false;
            return player.position.toLowerCase() === this.activeFilter;
          });
        }
        
        this.filteredPlayers = filtered;
      },
      
      goToPage(page) {
        if (page >= 1 && page <= this.totalPages) {
          this.currentPage = page;
          // Scroll to top of player list
          const playerList = document.querySelector('.players-list');
          if (playerList) playerList.scrollIntoView({ behavior: 'smooth' });
        }
      },
      
      invitePlayers() {
        if (!this.isTeamOwner) {
          this.showOwnerActionDeniedToast();
          return;
        }
        this.$router.push({
          name: "BasketballTeamDashboard",
          query: { tab: "invites", compose: "1" },
        });
      },

      onEmptyPlayersPrimaryAction() {
        if (this.searchQuery) {
          this.searchQuery = "";
          this.filterPlayers();
        }
      },

      editPlayer(player) {
        if (this.savingPlayer) return;
        if (!this.isTeamOwner) {
          this.showOwnerActionDeniedToast();
          return;
        }

        this.editingPlayer = player;
        this.playerForm = {
          position: player.position || "",
        };
        this.showEditPlayer = true;
      },
      
      async updateCaptain(player) {
        if (this.savingPlayer || String(player.role || "").toLowerCase() === "owner") return;
        if (!this.isTeamOwner) {
          this.showOwnerActionDeniedToast();
          return;
        }
        
        this.savingPlayer = true;
        try {
          const result = await this.toggleCaptaincy(player.id);
          
          if (result) {
            // Remove captaincy from all other players
            if (result.is_captain) {
              this.players.forEach(p => {
                if (p.id !== player.id && p.is_captain) {
                  p.is_captain = false;
                  p.role = 'player';
                }
              });
            }
            
            // Update the target player
            player.is_captain = result.is_captain;
            player.role = result.role;
            
            this.showToast('success', 
              player.is_captain 
                ? `${player.name} is now captain` 
                : 'Captain status removed'
            );
          }
        } catch (error) {
          if (error.message === OWNER_ACTION_DENIED) return;
          console.error('Error updating captain:', error);
          this.showToast('error', error.message || 'Failed to update captain status');
        } finally {
          this.savingPlayer = false;
        }
      },
      
      async toggleCaptaincy(teamMemberNumber) {
        const token = await this.getAuthToken();
        if (!token) throw new Error('Authentication required');
        
        const response = await fetch(buildApiUrl(`/api/teams/basketball/member/captain/${teamMemberNumber}`), {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          if (response.status === 403) {
            this.showOwnerActionDeniedToast();
            throw new Error(OWNER_ACTION_DENIED);
          }
          const errorData = await response.json().catch(() => ({}));
          const msg = this.formatApiErrorDetail(errorData.detail) || `Toggle captaincy failed: ${response.status}`;
          throw new Error(msg);
        }
        
        return await response.json();
      },
      
      confirmDeletePlayer(player) {
        if (!this.isTeamOwner) {
          this.showOwnerActionDeniedToast();
          return;
        }
        if (String(player.role || "").toLowerCase() === "owner") {
          this.showToast("error", "Cannot remove the team owner");
          return;
        }
        
        this.playerToDelete = player;
        this.showConfirmDelete = true;
      },
      
      async deletePlayer() {
        if (!this.playerToDelete || this.savingPlayer) return;
        
        this.savingPlayer = true;
        this.showConfirmDelete = false;
        
        try {
          const result = await this.deleteTeamMember(this.playerToDelete.id);
          
          if (result) {
            this.players = this.players.filter(p => p.id !== this.playerToDelete.id);
            this.filterPlayers();
            this.showToast('success', result.message || 'Player removed successfully');
            this.playerToDelete = null;
          }
        } catch (error) {
          console.error('Error removing player:', error);
          this.handleApiError(error, 'remove player');
        } finally {
          this.savingPlayer = false;
        }
      },
      
      async savePlayer() {
        if (this.savingPlayer || !this.editingPlayer) return;
        if (!this.isTeamOwner) {
          this.showOwnerActionDeniedToast();
          return;
        }

        this.savingPlayer = true;
        try {
          const updateData = {};
          if (this.playerForm.position !== this.editingPlayer.position) {
            updateData.position = this.playerForm.position || null;
          }

          if (Object.keys(updateData).length === 0) {
            this.showToast("info", "No changes made to player");
            this.closePlayerModal();
            return;
          }

          const result = await this.updateTeamMember(this.editingPlayer.id, updateData);

          if (result) {
            const index = this.players.findIndex((p) => p.id === this.editingPlayer.id);
            if (index !== -1) {
              this.players[index] = {
                ...this.players[index],
                position: this.playerForm.position,
              };
            }
            this.showToast("success", "Player updated successfully");
            this.filterPlayers();
            this.closePlayerModal();
          }
        } catch (error) {
          console.error("Error saving player:", error);
          this.handleApiError(error, "save player");
        } finally {
          this.savingPlayer = false;
        }
      },

      closePlayerModal() {
        if (this.savingPlayer) return;

        this.showEditPlayer = false;
        this.editingPlayer = null;
        this.playerForm = {
          position: "",
        };
      },
      
      // ========== API METHODS ==========
      handleApiError(error, operation) {
        if (error.message === OWNER_ACTION_DENIED) return;
        const message = error.message?.toLowerCase() || '';
        
        if (message.includes('403') || message.includes('unauthorized')) {
          this.showToast('error', 'You are not authorized to perform this action.');
        } else if (message.includes('404') || message.includes('not found')) {
          this.showToast('error', 'Resource not found. Please refresh the page.');
        } else if (message.includes('422') || message.includes('validation')) {
          this.showToast('error', 'Invalid data. Please check all fields.');
        } else if (message.includes('500')) {
          this.showToast('error', 'Server error. Please try again later.');
        } else if (message.includes('already a member')) {
          this.showToast('error', 'This player is already a team member.');
        } else if (message.includes("don't have a team")) {
          this.showToast('error', 'You need to create a team first.');
        } else {
          this.showToast('error', `Failed to ${operation}. Please try again.`);
        }
      },
      
      async fetchMyTeamWithMembers() {
        const token = await this.getAuthToken();
        if (!token) {
          this.showToast("error", "Authentication required. Redirecting...");
          setTimeout(() => {
            this.$router.push({ name: "BasketballTeamDashboard" });
          }, 1500);
          throw new Error("Authentication required");
        }

        const url = buildApiUrl("/api/teams/basketball/my-team?include_members=true");
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          if (response.status === 401) {
            this.showToast("error", "Session expired. Please log in again.");
            this.$router.push({ name: "CustomerSignIn", query: { redirect: this.$route.fullPath } });
            throw new Error("Session expired");
          }
          this.showToast("error", "Failed to fetch your team. Redirecting...");
          setTimeout(() => {
            this.$router.push({ name: "BasketballTeamDashboard" });
          }, 1500);
          throw new Error(`Failed to fetch team: ${response.status}`);
        }

        const data = await response.json();
        if (!data || data.team_id == null) {
          this.showToast("error", "No team found. Redirecting...");
          setTimeout(() => {
            this.$router.push({ name: "BasketballTeamDashboard" });
          }, 1500);
          return null;
        }

        return data;
      },

      formatApiErrorDetail(detail) {
        if (detail == null) return "";
        if (typeof detail === "string") return detail;
        if (Array.isArray(detail)) {
          return detail
            .map((e) => (typeof e === "object" && e.msg ? e.msg : JSON.stringify(e)))
            .join("; ");
        }
        if (typeof detail === "object" && detail.msg) return detail.msg;
        try {
          return JSON.stringify(detail);
        } catch {
          return "Request failed";
        }
      },

      async updateTeam(teamId, formData) {
        const token = await this.getAuthToken();
        if (!token) throw new Error("Authentication required");

        const response = await fetch(buildApiUrl(`/api/teams/basketball/${teamId}`), {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        });

        if (!response.ok) {
          if (response.status === 403) {
            this.showOwnerActionDeniedToast();
            throw new Error(OWNER_ACTION_DENIED);
          }
          const errorData = await response.json().catch(() => ({}));
          const msg = this.formatApiErrorDetail(errorData.detail) || `Update failed: ${response.status}`;
          throw new Error(msg);
        }

        return await response.json();
      },
      
      async updateTeamMember(teamMemberNumber, updateData) {
        const token = await this.getAuthToken();
        if (!token) throw new Error('Authentication required');
        
        const response = await fetch(buildApiUrl(`/api/teams/basketball/member/update/${teamMemberNumber}`), {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updateData),
        });

        if (!response.ok) {
          if (response.status === 403) {
            this.showOwnerActionDeniedToast();
            throw new Error(OWNER_ACTION_DENIED);
          }
          const errorData = await response.json().catch(() => ({}));
          const msg = this.formatApiErrorDetail(errorData.detail) || `Update failed: ${response.status}`;
          throw new Error(msg);
        }
        
        return await response.json();
      },
      
      async deleteTeamMember(teamMemberNumber) {
        const token = await this.getAuthToken();
        if (!token) throw new Error('Authentication required');
        
        const response = await fetch(buildApiUrl(`/api/teams/basketball/member/delete/${teamMemberNumber}`), {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          if (response.status === 403) {
            this.showOwnerActionDeniedToast();
            throw new Error(OWNER_ACTION_DENIED);
          }
          const errorData = await response.json().catch(() => ({}));
          const msg = this.formatApiErrorDetail(errorData.detail) || `Delete failed: ${response.status}`;
          throw new Error(msg);
        }
        
        return await response.json();
      }
    }
  };
  </script>

<style scoped>
/* Loading overlay - kept for fallback */
.basketball-edit-page .page-header h1 {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
}

.sport-pill--basketball {
  display: inline-block;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  background: linear-gradient(135deg, #e65100, #ff9800);
  color: #fff;
}

.basketball-edit-page .btn-primary {
  background: linear-gradient(135deg, #e65100, #f57c00);
  border-color: #e65100;
}

.basketball-edit-page .skill-pill.active,
.basketball-edit-page .day-pill.active,
.basketball-edit-page .tab-btn.active {
  border-color: #ff9800;
  background: rgba(255, 152, 0, 0.15);
  color: var(--text-color1);
}

.team-edit-page {
  background: var(--background-color1);
  min-height: 100vh;
}
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.loading-content {
  text-align: center;
  color: white;
}

.spinner-ring {
  display: inline-block;
  width: 64px;
  height: 64px;
  border: 4px solid var(--border-color-hover);
  border-radius: 50%;
  border-top-color: transparent;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Page Header */
.page-header {
  padding: 80px 2rem 2rem;
  margin: 0;
  background: var(--background-color2);
  border-bottom: 1px solid var(--background-color3);
}

.page-header-row {
  margin-bottom: 1rem;
}

.back-to-dashboard {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--border-color-hover);
  text-decoration: none;
  padding: 0.35rem 0;
  transition: opacity 0.2s ease, color 0.2s ease;
}

.back-to-dashboard:hover {
  color: var(--text-color1);
  opacity: 0.95;
}

.page-header h1 {
  font-size: clamp(1.5rem, 4vw, 2rem);
  font-weight: 700;
  margin-bottom: 0.5rem;
  color: var(--text-color1);
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.page-header p {
  color: var(--text-color2);
  font-size: clamp(0.875rem, 2vw, 1rem);
  margin: 0;
}

/* Main Content */
.edit-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;
  display: grid;
  gap: 2rem;
  grid-template-columns: 1fr;
}

@media (min-width: 1024px) {
  .edit-content {
    grid-template-columns: 1.5fr 1fr;
  }
}

@media (min-width: 1400px) {
  .edit-content {
    grid-template-columns: 2fr 1fr;
  }
}

/* Cards */
.card {
  background: var(--background-color2);
  border: 1px solid var(--background-color3);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px var(--box-shadow);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px var(--box-shadow);
}

.card-header {
  padding: 1.5rem;
  border-bottom: 1px solid var(--background-color3);
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: center;
  justify-content: space-between;
  background: linear-gradient(135deg, var(--background-color2), var(--background-color1));
}

.card-title {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.card-title h2 {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
  color: var(--text-color1);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.card-title i {
  color: var(--border-color-hover);
  font-size: 1.125rem;
}

.card-body {
  padding: 1.5rem;
}

@media (max-width: 768px) {
  .card-header {
    flex-direction: column;
    align-items: stretch;
  }
  
  .card-title {
    justify-content: center;
    margin-bottom: 1rem;
  }
}

/* Forms */
.team-form .form-grid {
  display: grid;
  gap: 1.5rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: var(--text-color1);
}

.form-group label i {
  margin-right: 0.5rem;
  color: var(--border-color-hover);
}

.player-readonly-name {
  margin: 0.25rem 0 0;
  font-weight: 600;
  color: var(--text-color1);
}

.player-readonly-meta {
  margin: 0.15rem 0 0;
  font-size: 0.875rem;
  color: var(--text-color2, #aaa);
}

.input-wrapper {
  position: relative;
  width: 100%;
}

.form-group input,
.form-group select {
  width: 100%;
  padding: 0.75rem 2.5rem 0.75rem 1rem;
  font-size: 0.875rem;
  background: var(--background-color1);
  border: 1px solid var(--border-color-hover);
  border-radius: 8px;
  color: var(--text-color1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  background-size: 12px;
}

.form-group textarea {
  width: 100%;
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  background: var(--background-color1);
  border: 1px solid var(--border-color-hover);
  border-radius: 8px;
  color: var(--text-color1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  resize: vertical;
  min-height: 100px;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: var(--border-color-hover);
  box-shadow: 0 0 0 3px var(--border-color-hover1), 0 2px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.form-group input.success,
.form-group select.success,
.form-group textarea.success {
  border-color: #10b981;
  padding-right: 2.5rem;
}

.form-group input.error,
.form-group select.error,
.form-group textarea.error {
  border-color: #ef4444;
  animation: shake 0.3s ease;
}

.input-success-icon {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #10b981;
  font-size: 1rem;
  pointer-events: none;
  animation: fadeInScale 0.3s ease;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-4px); }
  75% { transform: translateX(4px); }
}

@keyframes fadeInScale {
  0% {
    opacity: 0;
    transform: translateY(-50%) scale(0.8);
  }
  100% {
    opacity: 1;
    transform: translateY(-50%) scale(1);
  }
}

.error-message {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  color: #ef4444;
  margin-top: 0.5rem;
  animation: slideDown 0.3s ease;
}

.error-message i {
  font-size: 0.875rem;
}

@keyframes slideDown {
  0% {
    opacity: 0;
    transform: translateY(-4px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Logo Display */
.logo-display-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
}

.logo-display {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: var(--background-color1);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 2px solid var(--border-color-hover);
  overflow: hidden;
}

.logo-display i {
  font-size: 1.5rem;
  color: var(--text-color2);
}

.logo-display span {
  font-size: 0.625rem;
  color: var(--text-color2);
  margin-top: 0.25rem;
}

.logo-image-display {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid var(--border-color-hover);
}

.logo-info {
  font-size: 0.75rem;
  color: var(--text-color2);
  text-align: center;
  margin: 0;
}

/* Skill Levels */
.skill-levels {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 0.5rem;
}

.skill-pill {
  padding: 0.75rem 1rem;
  background: var(--background-color1);
  border: 1px solid var(--background-color3);
  border-radius: 8px;
  color: var(--text-color1);
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  text-align: center;
  position: relative;
  overflow: hidden;
}

.skill-pill::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: rgba(var(--border-color-hover-rgb), 0.1);
  transform: translate(-50%, -50%);
  transition: width 0.4s, height 0.4s;
}

.skill-pill:hover::before {
  width: 200px;
  height: 200px;
}

.skill-pill:hover {
  border-color: var(--border-color-hover);
  transform: translateY(-2px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.skill-pill.active {
  background: var(--border-color-hover1);
  border-color: var(--border-color-hover);
  color: var(--border-color-hover);
  font-weight: 500;
  box-shadow: 0 2px 8px rgba(var(--border-color-hover-rgb), 0.2);
}

/* Character Count */
.char-count {
  text-align: right;
  font-size: 0.75rem;
  color: var(--text-color2);
  margin-top: 0.25rem;
}

/* Form Sections */
.form-section {
  padding: 1.5rem;
  background: var(--background-color1);
  border-radius: 8px;
  border: 1px solid var(--background-color3);
  margin-top: 1rem;
}

.form-section h3 {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: var(--text-color1);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.form-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
}

/* Days Grid */
.days-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(60px, 1fr));
  gap: 0.5rem;
}

.day-pill {
  padding: 0.75rem 0.5rem;
  background: var(--background-color2);
  border: 1px solid var(--background-color3);
  border-radius: 8px;
  color: var(--text-color1);
  font-size: 0.75rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.day-pill::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: rgba(var(--border-color-hover-rgb), 0.1);
  transform: translate(-50%, -50%);
  transition: width 0.4s, height 0.4s;
}

.day-pill:hover::before {
  width: 150px;
  height: 150px;
}

.day-pill:hover {
  border-color: var(--border-color-hover);
  transform: translateY(-2px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

.day-pill.active {
  background: var(--border-color-hover1);
  border-color: var(--border-color-hover);
  color: var(--border-color-hover);
  font-weight: 500;
  box-shadow: 0 2px 6px rgba(var(--border-color-hover-rgb), 0.2);
}

/* Form Actions */
.form-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--background-color3);
  margin-top: 1.5rem;
}

/* Buttons */
.btn {
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  border-radius: 6px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
  border: 1px solid transparent;
  min-height: 44px;
}

@media (max-width: 480px) {
  .btn {
    width: 100%;
  }
  
  .form-actions {
    flex-direction: column;
  }
}

.btn-primary {
  background: var(--border-color-hover);
  color: var(--button-color);
  border-color: var(--border-color-hover);
  position: relative;
  overflow: hidden;
}

.btn-primary::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  transform: translate(-50%, -50%);
  transition: width 0.6s, height 0.6s;
}

.btn-primary:hover:not(:disabled)::before {
  width: 300px;
  height: 300px;
}

.btn-primary:hover:not(:disabled) {
  opacity: 0.95;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.btn-primary:active:not(:disabled) {
  transform: translateY(0);
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.btn-outline {
  background: transparent;
  color: var(--text-color1);
  border-color: var(--button-border-color);
}

.btn-outline:hover:not(:disabled) {
  background: var(--background-color3);
  border-color: var(--border-color-hover);
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.btn-outline:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-danger {
  background: #ef4444;
  color: white;
  border-color: #ef4444;
}

.btn-danger:hover:not(:disabled) {
  background: #dc2626;
  border-color: #dc2626;
}

.btn-icon {
  padding: 0.5rem;
  background: transparent;
  border: 1px solid var(--background-color3);
  border-radius: 8px;
  color: var(--text-color1);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  min-height: 36px;
  position: relative;
  overflow: hidden;
}

.btn-icon::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.1);
  transform: translate(-50%, -50%);
  transition: width 0.4s, height 0.4s;
}

.btn-icon:hover:not(:disabled)::before {
  width: 100px;
  height: 100px;
}

.btn-icon:hover:not(:disabled) {
  background: var(--background-color3);
  border-color: var(--border-color-hover);
  color: var(--border-color-hover);
  transform: translateY(-2px) scale(1.05);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.btn-icon:active:not(:disabled) {
  transform: translateY(0) scale(1);
}

.btn-icon.btn-edit:hover:not(:disabled) {
  background: rgba(59, 130, 246, 0.1);
  border-color: #3b82f6;
  color: #3b82f6;
}

.btn-icon.btn-captain:hover:not(:disabled) {
  background: rgba(251, 191, 36, 0.1);
  border-color: #fbbf24;
  color: #fbbf24;
}

.btn-icon.btn-captain.is-captain {
  background: rgba(251, 191, 36, 0.15);
  border-color: #fbbf24;
  color: #fbbf24;
}

.btn-icon.btn-danger:hover:not(:disabled) {
  background: rgba(239, 68, 68, 0.1);
  border-color: #ef4444;
  color: #ef4444;
}

.btn-icon:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

/* Members Controls */
.members-controls {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.search-box {
  position: relative;
}

.search-box i {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-color2);
}

.search-box input {
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  background: var(--background-color1);
  border: 1px solid var(--background-color3);
  border-radius: 8px;
  color: var(--text-color1);
  font-size: 0.875rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.search-box input:focus {
  outline: none;
  border-color: var(--border-color-hover);
  box-shadow: 0 0 0 3px var(--border-color-hover1), 0 2px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.clear-search {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: var(--text-color2);
  cursor: pointer;
  padding: 0.25rem;
}

.filter-tabs {
  display: flex;
  gap: 0.5rem;
  overflow-x: auto;
  padding-bottom: 0.25rem;
  scrollbar-width: thin;
}

.tab-btn {
  padding: 0.5rem 1rem;
  background: var(--background-color1);
  border: 1px solid var(--background-color3);
  border-radius: 20px;
  color: var(--text-color1);
  font-size: 0.75rem;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
}

.tab-btn:hover:not(.active) {
  background: var(--background-color2);
  border-color: var(--border-color-hover);
  transform: translateY(-1px);
}

.tab-btn.active {
  background: var(--border-color-hover1);
  border-color: var(--border-color-hover);
  color: var(--border-color-hover);
  font-weight: 500;
  box-shadow: 0 2px 6px rgba(var(--border-color-hover-rgb), 0.2);
}

.tab-count {
  background: var(--background-color3);
  padding: 0.125rem 0.375rem;
  border-radius: 10px;
  font-size: 0.625rem;
  margin-left: 0.25rem;
}

/* Players List */
.players-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-height: 200px;
}

.player-card {
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
  padding: 1.5rem;
  background: var(--background-color1);
  border: 1px solid var(--background-color3);
  border-radius: 12px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.player-card::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 0;
  background: linear-gradient(90deg, transparent, rgba(var(--border-color-hover-rgb), 0.1), transparent);
  transition: width 0.5s ease;
}

.player-card:hover::before {
  width: 100%;
}

.player-card:hover {
  border-color: var(--border-color-hover);
  transform: translateX(6px) translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  background: var(--background-color2);
}

.player-card.is-captain {
  border-left: 4px solid #fbbf24;
  background: linear-gradient(90deg, rgba(251, 191, 36, 0.05), var(--background-color1));
}

.player-card.is-captain:hover {
  background: linear-gradient(90deg, rgba(251, 191, 36, 0.1), var(--background-color2));
}

.player-card.is-owner {
  border-left: 4px solid #8b5cf6;
  background: linear-gradient(90deg, rgba(139, 92, 246, 0.08), var(--background-color1));
  padding: 1.75rem;
  gap: 1.75rem;
}

.player-card.is-owner:hover {
  background: linear-gradient(90deg, rgba(139, 92, 246, 0.12), var(--background-color2));
}

.player-card.is-owner .player-avatar {
  width: 72px;
  height: 72px;
  font-size: 2rem;
  border-width: 3px;
}

.player-card.is-owner .player-info {
  gap: 1.25rem;
}

.player-card.is-owner .player-header h4 {
  font-size: 1.25rem;
  margin-bottom: 1rem;
}

.player-card.is-owner .player-meta {
  gap: 1rem;
  margin-bottom: 0.5rem;
}

  .player-card.is-owner .player-stats-grid {
    gap: 0.75rem;
    margin-top: 0.5rem;
  }

  .player-card.is-owner .stat-item {
    padding: 0.5rem 0.75rem;
    min-width: 80px;
  }

/* Player Avatar */
.player-avatar {
  position: relative;
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, var(--background-color2), var(--background-color1));
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--border-color-hover);
  font-size: 1.75rem;
  border: 2px solid var(--border-color-hover);
  flex-shrink: 0;
  margin-top: 0.25rem;
}

.captain-badge {
  position: absolute;
  bottom: -4px;
  right: -4px;
  background: linear-gradient(135deg, #FFD700, #FFA500);
  color: #000;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  border: 2px solid var(--background-color2);
}

.player-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.player-header {
  margin-bottom: 0;
}

.player-header h4 {
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0 0 0.75rem;
  color: var(--text-color1);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.player-meta {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  flex-wrap: wrap;
  margin-bottom: 0.25rem;
}

.player-position {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.25rem 0.625rem;
  background: rgba(var(--border-color-hover-rgb), 0.1);
  border-radius: 12px;
  color: var(--border-color-hover);
  border: 1px solid rgba(var(--border-color-hover-rgb), 0.2);
}

.position-icon {
  font-size: 0.7rem;
  opacity: 0.8;
}

.player-role {
  padding: 0.25rem 0.625rem;
  background: var(--role-item);
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--text-color1);
}

/* Player Stats Grid */
.player-stats-grid {
  display: grid;
  gap: 0.5rem;
  margin-top: 0;
}

.player-stats-grid.stats-all {
  grid-template-columns: repeat(3, 1fr);
}

.player-stats-grid.stats-two {
  grid-template-columns: repeat(2, 1fr);
  max-width: 160px;
}

.player-stats-grid.stats-one {
  grid-template-columns: 1fr;
  max-width: 90px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.5rem;
  background: var(--background-color3);
  border-radius: 6px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid transparent;
  min-width: 60px;
  width: 100%;
}

.stat-item:hover {
  background: var(--background-color2);
  border-color: var(--border-color-hover);
  transform: translateY(-2px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

.stat-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  background: var(--background-color2);
  border-radius: 4px;
  color: var(--border-color-hover);
  font-size: 0.625rem;
  flex-shrink: 0;
}

.stat-content {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
  overflow: hidden;
}

.stat-label {
  font-size: 0.5625rem;
  font-weight: 500;
  color: var(--text-color2);
  white-space: nowrap;
  line-height: 1.1;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
}

.stat-value {
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--text-color1);
  white-space: nowrap;
  line-height: 1.1;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
}

.player-actions {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  flex-shrink: 0;
  align-self: flex-start;
  margin-top: 0.25rem;
}

/* Responsive Player Cards */
@media (max-width: 1024px) {
  .player-card {
    gap: 1.25rem;
    padding: 1.25rem;
  }
  
  .player-avatar {
    width: 60px;
    height: 60px;
    font-size: 1.625rem;
  }
  
  .player-header h4 {
    font-size: 1.0625rem;
  }
  
  .player-stats-grid.stats-two {
    max-width: 140px;
  }
  
  .player-stats-grid.stats-one {
    max-width: 80px;
  }
  
  .player-card.is-owner {
    padding: 1.5rem;
    gap: 1.5rem;
  }
  
  .player-card.is-owner .player-avatar {
    width: 68px;
    height: 68px;
    font-size: 1.875rem;
  }
  
  .player-card.is-owner .player-stats-grid.stats-two {
    max-width: 160px;
  }
  
  .player-card.is-owner .player-stats-grid.stats-one {
    max-width: 100px;
  }
}

@media (max-width: 768px) {
  .player-card {
    gap: 1rem;
    padding: 1.125rem;
  }
  
  .player-info {
    gap: 0.875rem;
  }
  
  .player-header h4 {
    font-size: 1rem;
    margin-bottom: 0.625rem;
  }
  
  .player-meta {
    gap: 0.625rem;
  }
  
  .player-stats-grid {
    gap: 0.875rem;
  }
  
  .player-stats-grid.stats-all {
    grid-template-columns: repeat(3, 1fr);
  }
  
  .player-stats-grid.stats-two {
    grid-template-columns: repeat(2, 1fr);
    max-width: 100%;
  }
  
  .player-stats-grid.stats-one {
    grid-template-columns: 1fr;
    max-width: 100%;
  }
  
  .stat-item {
    padding: 0.5rem 0.625rem;
    gap: 0.5rem;
    min-width: 60px;
  }
  
  .stat-icon {
    width: 18px;
    height: 18px;
    font-size: 0.5625rem;
  }
  
  .stat-label {
    font-size: 0.5rem;
  }
  
  .stat-value {
    font-size: 0.6875rem;
  }
  
  .player-card.is-owner {
    padding: 1.375rem;
    gap: 1.5rem;
  }
  
  .player-card.is-owner .player-avatar {
    width: 64px;
    height: 64px;
    font-size: 1.75rem;
  }
  
  .player-card.is-owner .stat-item {
    min-width: 80px;
  }
  
  .stat-icon {
    width: 28px;
    height: 28px;
    font-size: 0.8125rem;
  }
  
  .stat-label {
    font-size: 0.625rem;
  }
  
  .stat-value {
    font-size: 0.9375rem;
  }
}

@media (max-width: 640px) {
  .player-card {
    flex-direction: column;
    text-align: center;
    gap: 1.25rem;
    padding: 1.25rem;
    align-items: center;
  }
  
  .player-avatar {
    width: 72px;
    height: 72px;
    font-size: 2rem;
    margin-top: 0;
  }
  
  .player-info {
    width: 100%;
    align-items: center;
    gap: 1rem;
  }
  
  .player-header {
    width: 100%;
  }
  
  .player-header h4 {
    font-size: 1.125rem;
    white-space: normal;
    text-align: center;
  }
  
  .player-meta {
    justify-content: center;
    width: 100%;
    gap: 0.75rem;
  }
  
  .player-stats-grid {
    width: 100%;
    justify-content: center;
    gap: 0.75rem;
  }
  
  .player-stats-grid.stats-all {
    grid-template-columns: repeat(3, 1fr);
    max-width: 100%;
  }
  
  .player-stats-grid.stats-two {
    grid-template-columns: repeat(2, 1fr);
    max-width: 100%;
  }
  
  .player-stats-grid.stats-one {
    grid-template-columns: 1fr;
    max-width: 100px;
    margin: 0 auto;
  }
  
  .stat-item {
    padding: 0.5rem;
    justify-content: center;
    min-width: 60px;
  }
  
  .player-card.is-owner {
    padding: 1.5rem;
    gap: 1.5rem;
  }
  
  .player-card.is-owner .player-avatar {
    width: 80px;
    height: 80px;
    font-size: 2.25rem;
  }
  
  .player-card.is-owner .player-header h4 {
    font-size: 1.25rem;
  }
  
  .player-card.is-owner .stat-item {
    min-width: 80px;
  }
  
  .player-actions {
    width: 100%;
    flex-direction: row;
    justify-content: center;
    align-self: center;
    margin-top: 0;
    gap: 0.75rem;
  }
}

@media (max-width: 480px) {
  .player-card {
    padding: 1rem;
    gap: 1rem;
  }
  
  .player-avatar {
    width: 64px;
    height: 64px;
    font-size: 1.75rem;
  }
  
  .player-header h4 {
    font-size: 1rem;
  }
  
  .player-stats-grid.stats-all {
    grid-template-columns: repeat(3, 1fr);
    gap: 0.5rem;
  }
  
  .player-stats-grid.stats-two {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.5rem;
  }
  
  .stat-item {
    padding: 0.375rem;
    gap: 0.375rem;
    flex-direction: column;
    text-align: center;
    min-width: 50px;
  }
  
  .stat-icon {
    width: 18px;
    height: 18px;
    font-size: 0.5625rem;
  }
  
  .stat-label {
    font-size: 0.5rem;
  }
  
  .stat-value {
    font-size: 0.6875rem;
  }
  
  .player-actions {
    gap: 0.5rem;
  }
  
  .player-card.is-owner {
    padding: 1.25rem;
    gap: 1.25rem;
  }
  
  .player-card.is-owner .player-avatar {
    width: 72px;
    height: 72px;
    font-size: 2rem;
  }
  
  .player-card.is-owner .stat-item {
    min-width: 70px;
  }
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 4rem 1rem;
  color: var(--text-color2);
  animation: fadeInUp 0.5s ease;
}

.empty-icon {
  font-size: 4rem;
  margin-bottom: 1.5rem;
  color: var(--background-color3);
  opacity: 0.6;
  animation: float 3s ease-in-out infinite;
}

.empty-state h3 {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  color: var(--text-color1);
}

.empty-state .empty-description {
  margin-bottom: 2rem;
  color: var(--text-color2);
  font-size: 0.9375rem;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
}

@keyframes fadeInUp {
  0% {
    opacity: 0;
    transform: translateY(20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes float {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

/* Pagination */
.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--background-color3);
  flex-wrap: wrap;
}

.pagination-btn {
  padding: 0.5rem 0.75rem;
  background: var(--background-color1);
  border: 1px solid var(--background-color3);
  border-radius: 8px;
  color: var(--text-color1);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  min-width: 36px;
  min-height: 36px;
}

.pagination-btn:hover:not(:disabled) {
  border-color: var(--border-color-hover);
  color: var(--border-color-hover);
  background: var(--background-color2);
  transform: translateY(-2px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.pagination-btn:active:not(:disabled) {
  transform: translateY(0);
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-numbers {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.page-btn {
  min-width: 36px;
  min-height: 36px;
  padding: 0.5rem;
  background: var(--background-color1);
  border: 1px solid var(--background-color3);
  border-radius: 8px;
  color: var(--text-color1);
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
}

.page-btn:hover:not(.active):not(:disabled) {
  border-color: var(--border-color-hover);
  background: var(--background-color2);
  transform: translateY(-1px);
}

.page-btn.active {
  background: var(--border-color-hover1);
  border-color: var(--border-color-hover);
  color: var(--border-color-hover);
  font-weight: 600;
  box-shadow: 0 2px 6px rgba(var(--border-color-hover-rgb), 0.2);
}

.page-ellipsis {
  padding: 0.5rem;
  color: var(--text-color2);
  font-size: 0.875rem;
}

/* Confirmation Modal Content */
.confirmation-content {
  text-align: center;
  padding: 1rem;
}

.warning-icon {
  font-size: 3rem;
  color: #f59e0b;
  margin-bottom: 1rem;
}

.warning-text {
  color: #ef4444;
  font-size: 0.875rem;
  margin-top: 0.5rem;
}

/* Animations */
.slide-in {
  animation: slideIn 0.3s ease;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.list-enter-active,
.list-leave-active {
  transition: all 0.3s ease;
}

.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}

.list-move {
  transition: transform 0.3s ease;
}

/* Responsive Design Improvements */
@media (max-width: 768px) {
  .page-header {
    padding: 4rem 1rem 1.5rem;
  }
  
  .edit-content {
    padding: 1rem;
    gap: 1.5rem;
  }
  
  .card-header {
    padding: 1rem;
  }
  
  .card-body {
    padding: 1rem;
  }
  
  .form-section {
    padding: 1rem;
  }
  
  .days-grid {
    grid-template-columns: repeat(4, 1fr);
  }
  
  .player-stats-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (min-width: 769px) and (max-width: 1024px) {
  .edit-content {
    grid-template-columns: 1fr;
  }
}

/* Touch Device Improvements */
@media (hover: none) and (pointer: coarse) {
  .btn,
  .skill-pill,
  .day-pill,
  .tab-btn,
  .btn-icon,
  .page-btn,
  .pagination-btn {
    min-height: 48px;
  }
  
  .player-card {
    padding: 1.25rem;
  }
}

/* Print Styles */
@media print {
  .card {
    border: 1px solid #ddd;
    box-shadow: none;
  }
  
  .btn,
  .player-actions,
  .members-controls,
  .pagination {
    display: none;
  }
}
</style>
